import { useEffect, useRef, useState } from "react";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getDatabase, onValue, ref } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { db } from "../../../../../firebase";
import { managedOffersRequest, normalizeOffers } from "../../data/managedOffersApi";

// Observe individual authorized orders, including while the list/detail is closed.
export default function useBookingOfferIndicators(bookings, token, actorId, onOffer) {
  const [remote, setRemote] = useState({});
  const controller = useRef(null);
  const latest = useRef({ bookings, onOffer });
  latest.current = { bookings, onOffer };
  const idsKey = JSON.stringify(bookings
    .filter((booking) => !(Number(booking.idDriver) > 0) &&
      !["FINISH", "RUNNING", "CANCEL"].includes(booking.statusService))
    .map((booking) => booking.uuid).filter(Boolean).sort());

  useEffect(() => {
    if (!token || !actorId) return undefined;
    let disposed = false;
    let readApp;
    let session;
    let disconnect;
    const entries = new Map();
    const isCurrent = (entry) => !disposed && entries.get(entry.id) === entry;
    const publish = () => {
      if (disposed) return;
      const indicators = {};
      entries.forEach((entry, id) => {
        const now = entry.anchor
          ? entry.anchor.server + performance.now() - entry.anchor.monotonic
          : Infinity;
        const active = entry.snapshot?.state === "SEARCHING"
          ? normalizeOffers(entry.snapshot, now) : [];
        indicators[id] = { count: active.length, loading: !entry.snapshot && !entry.error, error: entry.error };
        const newOffers = active.filter((offer) => !entry.seen.has(offer.offerId));
        active.forEach((offer) => entry.seen.add(offer.offerId));
        if (newOffers.length) {
          const booking = latest.current.bookings.find((item) => item.uuid === id);
          if (booking) latest.current.onOffer?.(booking, newOffers.length);
        }
      });
      setRemote((previous) => {
        if (previous.actorId === actorId && previous.token === token &&
            JSON.stringify(previous.indicators) === JSON.stringify(indicators)) return previous;
        return { actorId, token, indicators };
      });
    };
    const apply = (entry, snapshot, started) => {
      if (!isCurrent(entry) || !snapshot || snapshot.bookingId !== entry.id || snapshot.bookingKind !== "BUSINESS") return;
      if (started != null && snapshot.serverTime) {
        entry.anchor = { server: Number(snapshot.serverTime), monotonic: (started + performance.now()) / 2 };
      }
      if (!entry.snapshot || Number(snapshot.revision) >= Number(entry.snapshot.revision)) entry.snapshot = snapshot;
      publish();
    };
    const ensureSession = () => {
      if (!session) {
        session = (async () => {
          const credentials = await managedOffersRequest(token, "post", "session/firebase-token");
          if (disposed) return null;
          readApp = initializeApp(db.app.options, `business-offer-list-${uuidv4()}`);
          await signInWithCustomToken(getAuth(readApp), credentials.customToken);
          if (disposed) return null;
          const readDb = getDatabase(readApp);
          disconnect = onValue(ref(readDb, ".info/connected"), (value) => {
            if (value.val()) entries.forEach(refresh);
          });
          return readDb;
        })().catch(async (failure) => {
          if (readApp) { await deleteApp(readApp).catch(() => {}); readApp = null; }
          session = null;
          throw failure;
        });
      }
      return session;
    };
    const attach = async (entry) => {
      if (!isCurrent(entry) || entry.unsubscribe || entry.attaching) return;
      entry.attaching = true;
      try {
        const readDb = await ensureSession();
        if (!readDb || !isCurrent(entry)) return;
        entry.unsubscribe = onValue(ref(readDb, `counterOffersV2/bookings/BUSINESS/${entry.id}`),
          (value) => apply(entry, value.val()),
          () => {
            entry.unsubscribe?.();
            entry.unsubscribe = null;
            entry.error = "Actualizando ofertas por HTTP.";
            publish();
          });
      } catch (_) {
        if (isCurrent(entry)) { entry.error = "Actualizando ofertas por HTTP."; publish(); }
      } finally { entry.attaching = false; }
    };
    async function refresh(entry) {
      if (!isCurrent(entry) || entry.reading) return;
      entry.reading = true;
      const started = performance.now();
      try {
        const snapshot = await managedOffersRequest(token, "get", `bookings/BUSINESS/${entry.id}`);
        if (!isCurrent(entry)) return;
        entry.error = "";
        apply(entry, snapshot, started);
      } catch (_) {
        if (isCurrent(entry)) {
          entry.error = "No se pudieron actualizar las ofertas de este pedido.";
          publish();
        }
      } finally {
        entry.reading = false;
        if (isCurrent(entry)) attach(entry);
      }
    }
    controller.current = {
      sync(ids) {
        const wanted = new Set(ids);
        entries.forEach((entry, id) => {
          if (!wanted.has(id)) { entries.delete(id); entry.unsubscribe?.(); }
        });
        ids.forEach((id) => {
          if (entries.has(id)) return;
          const entry = { id, seen: new Set(), error: "" };
          entries.set(id, entry);
          refresh(entry);
        });
        publish();
      },
    };
    const poll = setInterval(() => entries.forEach(refresh), 5000);
    const clock = setInterval(publish, 500);
    return () => {
      disposed = true;
      controller.current = null;
      entries.forEach((entry) => entry.unsubscribe?.());
      disconnect?.();
      clearInterval(poll);
      clearInterval(clock);
      if (readApp) deleteApp(readApp).catch(() => {});
    };
  }, [token, actorId]);

  useEffect(() => { controller.current?.sync(JSON.parse(idsKey)); }, [idsKey, token, actorId]);

  return remote.actorId === actorId && remote.token === token ? remote.indicators || {} : {};
}
